import { useEffect, useState } from 'react'
import { Alert, Box, Button, MenuItem, Skeleton, Stack, TextField } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { accountingApi } from '@/api/accounting'
import { getErrorMessage } from '@/utils/error'
import { TAX_REGIMES, type TaxRegime } from '@/types/accounting'

/**
 * Налоговые настройки учителя: режим, ставка и валюта.
 *
 * Настройка одна и действует бессрочно — периода действия здесь нет: расчёт
 * налога всё равно берёт единственную запись учителя, а поля с датами обещали
 * бы фильтрацию по периодам, которой не происходит.
 */
export function TaxSettingsTab() {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [regime, setRegime] = useState<TaxRegime>('none')
  const [customRate, setCustomRate] = useState('')
  const [currency, setCurrency] = useState('RUB')

  useEffect(() => {
    let active = true
    accountingApi
      .getTaxSettings()
      .then((s) => {
        if (!active) return
        setRegime(s.regime)
        setCustomRate(s.custom_rate ?? '')
        setCurrency(s.currency || 'RUB')
      })
      .catch((err) => setError(getErrorMessage(err) || t('common.error')))
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [t])

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    setSuccess(false)
    try {
      await accountingApi.updateTaxSettings({
        regime,
        custom_rate: regime === 'custom' ? (customRate === '' ? null : Number(customRate)) : null,
        currency,
      })
      setSuccess(true)
    } catch (err) {
      setError(getErrorMessage(err) || t('common.error'))
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Skeleton variant="rounded" height={320} />
  }

  return (
    <Box sx={{ maxWidth: 480 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          {t('accounting.tax.saved', 'Налоговые настройки сохранены')}
        </Alert>
      )}

      <Stack spacing={3} sx={{ pt: 1 }}>
        <TextField
          select
          label={t('accounting.tax.regime', 'Налоговый режим')}
          value={regime}
          onChange={(e) => setRegime(e.target.value as TaxRegime)}
          fullWidth
        >
          {TAX_REGIMES.map((r) => (
            <MenuItem key={r} value={r}>
              {t(`accounting.regime.${r}`, r)}
            </MenuItem>
          ))}
        </TextField>

        {regime === 'custom' && (
          <TextField
            label={t('accounting.tax.customRate', 'Ставка налога, %')}
            type="number"
            value={customRate}
            onChange={(e) => setCustomRate(e.target.value)}
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            fullWidth
          />
        )}

        <TextField
          label={t('accounting.tax.currency', 'Валюта')}
          value={currency}
          onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          inputProps={{ maxLength: 3 }}
          sx={{ maxWidth: 160 }}
        />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {t('common.save', 'Сохранить')}
          </Button>
        </Box>
      </Stack>
    </Box>
  )
}
